import {useInfiniteQuery, useQuery} from '@tanstack/react-query';
import {fetch, fetchRecords} from '../services/UsageEventsService';
import {PAGE_SIZE} from '../config';

// FNP-536: read-only module. No create/update/delete — the FNP-215 ingestion writer owns
// the UsageEvent lifecycle — so there are no mutations and nothing to invalidate.
export const usageEventsKeys = {
    all: ['usage-events'] as const,
    lists: () => [...usageEventsKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...usageEventsKeys.lists(), {filters}] as const,
    details: () => [...usageEventsKeys.all, 'detail'] as const,
    detail: (id: string) => [...usageEventsKeys.details(), id] as const,
};

// Set deliberately: the default of 0 refetches on every mount, and under an INFINITE query
// that is every page loaded so far — paging down, opening a detail and coming back would
// re-pay for the whole window. Reload is the explicit way to refresh.
const STALE_TIME = 30_000;

// Only the server-side filters are in the key. The keyword is matched client-side over the
// loaded pages (useUsageEvents), so putting it in the key would refetch page 1 on every
// search and throw the loaded pages away. No sort argument is sent: the resolver ignores
// `sort` and always returns newest-first.
export const useUsageEventsInfiniteQuery = (filters: Record<string, string>) => {
    return useInfiniteQuery({
        queryKey: usageEventsKeys.list(filters),
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            fetchRecords({limit: PAGE_SIZE, filter: filters, nextToken: pageParam}),
        // undefined, never null, when done — null makes react-query refetch forever.
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
        staleTime: STALE_TIME,
    });
};

export const useUsageEventQuery = (id?: string) => {
    return useQuery({
        queryKey: usageEventsKeys.detail(id ?? ''),
        queryFn: () => fetch(id!),
        enabled: !!id,
        staleTime: STALE_TIME,
    });
};
